import React, { useState, useEffect, useCallback, useRef, use } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { BiImport } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import partiesApi from '../data/party';
import stocksApi from '../data/stock';
import updatestocksApi from '../data/stockhistory';
import sellstocksApi from '../data/sellstocks';
import withdrawlApi from '../data/withdrawl';
import { fetchStocknames } from '../data/Stocknames';
import Select from "react-select";
// ---- Simple Calendar for in-line date filter ----
interface CalendarProps {
  value?: string;
  onChange: (val: string) => void;
  highlightedDates?: string[];
}
interface StockNames {
  id: number;
  name: string;
  screener: string;
  ltp: string;
  change_percent: string;
  open_price: string;
  volume: number;
  market_cap_cr: string;
  pe_ratio: string;
  industry_pe: string;
  high_52w: string;
  low_52w: string;
  returns_1m: string;
  returns_3m: string;
  returns_1y: string;
  returns_3y: string;
  returns_5y: string;
  pb_ratio: string;
  dividend: string;
  roe: string;
  roce: string;
  eps: string;
  dma_50: string;
  dma_200: string;
  rsi: string;
  margin_funding: string;
  margin_pledge: string;
  [key: string]: any;
}
const Calendar: React.FC<CalendarProps> = ({ value, onChange, highlightedDates = [] }) => {
  const today = new Date();
  const [current, setCurrent] = useState(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.valueOf())) return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const getDaysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const getWeekday = (y: number, m: number, d: number) => new Date(y, m, d).getDay();

  const selected = value ? new Date(value) : null;
  const year = current.getFullYear();
  const month = current.getMonth();
  const daysInMonth = getDaysInMonth(year, month);

  const highlights = React.useMemo(() => new Set(highlightedDates), [highlightedDates]);
  const firstWeekDay = getWeekday(year, month, 1);

  let grid: (string | null)[] = [];
  for (let i = 0; i < firstWeekDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`
    grid.push(dateStr);
  }
  while (grid.length % 7 !== 0) grid.push(null);

  return (
    <div className="shadow border rounded bg-white p-3 z-10" style={{ minWidth: 260 }}>
      <div className="flex justify-between items-center mb-2">
        <button
          className="bg-transparent text-gray-500 px-2"
          onClick={() => setCurrent(new Date(year, month - 1, 1))}
          type="button"
        >‹</button>
        <span className="font-semibold text-sm">{current.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <button
          className="bg-transparent text-gray-500 px-2"
          onClick={() => setCurrent(new Date(year, month + 1, 1))}
          type="button"
        >›</button>
      </div>
      <div className="grid grid-cols-7 mb-1 text-xs text-center">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => <div key={d} className="font-semibold">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 text-sm">
        {grid.map((dateStr, idx) => {
          if (dateStr === null) return <div key={idx} />;
          const d = new Date(dateStr);
          const displayDay = d.getDate();
          const isSelected = selected && dateStr === value;
          const highlight = highlights.has(dateStr);
          return (
            <button
              key={dateStr}
              type="button"
              className={`rounded-full w-8 h-8 mx-auto mb-1
                ${isSelected ? 'bg-[#1CAC70] text-white' : highlight ? 'bg-[#FFFAE5] text-black font-semibold border border-[#FF9500]' : 'bg-transparent hover:bg-[#F4F4F6]'}`}
              onClick={() => onChange(dateStr)}
            >
              {displayDay}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface Party {
  id: number;
  date: string;
  name: string;
  number: string;
  contact_person_name: string;
  contact_person_number: string;
  categories: string[];
  deleteparty: number;
}

interface StockCategoryObj {
  id?: number;
  category: string;
  stock_name: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
  current_price?: string;
  current_price_date?: string;
  investment_type?: string;  // Added for investment type filtering
}

interface Stock {
  id: number;
  date: string;
  party_id: number;
  total_amount?: number | string;
  indirect_charges?: number | string;
  categories: StockCategoryObj[];
  name?: string;
  date_of_purchase?: string;
  unit?: string;
  price_per_unit?: string | number;
  party_name?: string;
  indirct_expenses?: any;
  sale?: any;
  current_price?: any;
  current_price_date?: any;
  withdrawal?: number;
  current_status?: any;
  categoryObjs?: StockCategoryObj[];
  category?: string;
  cat_id?: number;
  sundry_creditors?: number;
}

interface Withdrawal {
  id: number;
  party_id: number;
  amount: number;
  date: string;
  catrgories: number;
}

// --- Added TypeScript interface for SellStockApi data ---
interface SellStockApiItem {
  id: number;
  party_id: number;
  category: string;
  stock_id: string;
  stock_name: string;
  sell_units: string;
  sell_amount: string;
  indirect_charges: string;
  date: string;
}

interface Props { }

type UpdateStatusModalProps = {
  show: boolean;
  stock: Stock | null;
  onClose: () => void;
  onUpdate: (updated: { id: number; cat_id?: number; current_price: string; current_price_date: string; dividend_amount?: string }) => void;
};

// Edit modal props
type EditStockModalProps = {
  show: boolean;
  stock: Stock | null;
  onClose: () => void;
  onUpdate: (updated:
    {
      id: number;
      cat_id?: number;
      name: string;
      party_id: number;
      categories: string;
      category: string;
      unit: string;
      price_per_unit: string | number;
      total_amount: string | number;
      indirect_charges: string | number;
      date: string;
      // You can add more fields as needed
    }) => void;
};

const getUnique = (arr: any[], field: string): string[] => {
  if (field === 'categories') {
    return Array.from(
      new Set(
        arr.flatMap(item =>
          Array.isArray(item.categories)
            ? item.categories.map((cat: any) => (typeof cat.category === "string" ? cat.category.trim() : "")).filter(Boolean)
            : []
        ).filter(Boolean)
      )
    )
  }
  return Array.from(
    new Set(
      arr.map(item => (item as any)[field])
        .filter(Boolean)
        .map((v: string) => v.toString().trim())
    )
  )
};

function findLatestStockHistory(
  updateStockRecords: any[],
  stock_id: number,
  cat_id?: number
) {
  const filtered = updateStockRecords.filter(
    rec =>
      rec.stock_id === stock_id &&
      String(rec.cat_id ?? "") === String(cat_id ?? "")
  );
  if (filtered.length === 0) return null;
  return filtered.reduce((latest, current) => {
    const latestTime = latest.current_price_date
      ? new Date(latest.current_price_date).getTime()
      : 0;
    const currentTime = current.current_price_date
      ? new Date(current.current_price_date).getTime()
      : 0;
    if (currentTime > latestTime) return current;
    if (currentTime === latestTime) {
      return (current.id ?? 0) > (latest.id ?? 0) ? current : latest;
    }
    return latest;
  }, filtered[0]);
}

const TABLE_COLUMNS = [
  { key: "party_name", label: "Party Name" },           // moved up
  { key: "category", label: "Category" },
  { key: "name", label: "Stock Name" },
  { key: "date", label: "Date of purchase" },
  { key: "unit", label: "Unit" },
  { key: "price_per_unit", label: "Price per unit" },
  { key: "total_amount", label: "Total Amount" },
  { key: "indirect_charges", label: "Indirct Expenses" },
  { key: "current_price", label: "Current Price" },
  { key: "current_price_date", label: "Current Price date" },
  { key: "withdrawal", label: "Withdrawal" },
  { key: "sale", label: "Sale" },
  { key: "current_status", label: "Current Status" },
];

// --- Edit Stock Modal ---
// ... unchanged code for EditStockModal ...

const EditStockModal: React.FC<EditStockModalProps> = ({ show, stock, onClose, onUpdate }) => {
  // ...body unchanged
  // (same as before)
  const [name, setName] = useState<string>(stock?.name ?? '');
  const [category, setCategory] = useState<string>(
    Array.isArray(stock?.categories) && stock?.categories.length
      ? stock.categories[0].category
      : ''
  );
  const [unit, setUnit] = useState<string>(stock?.unit ?? '');
  const [pricePerUnit, setPricePerUnit] = useState<string | number>(stock?.price_per_unit ?? '');
  const [totalAmount, setTotalAmount] = useState<string | number>(stock?.total_amount ?? '');
  const [indirectCharges, setIndirectCharges] = useState<string | number>(stock?.indirect_charges ?? '');
  const [date, setDate] = useState<string>(stock?.date ? stock.date.slice(0, 10) : '');
  const [stocksnames, setStocksnames] = useState<StockNames[]>([]);

  React.useEffect(() => {
    setName(stock?.name ?? '');
    setCategory(Array.isArray(stock?.categories) && stock?.categories.length ? stock.categories[0].category : '');
    setUnit(stock?.unit ?? '');
    setPricePerUnit(stock?.price_per_unit ?? '');
    setTotalAmount(stock?.total_amount ?? '');
    setIndirectCharges(stock?.indirect_charges ?? '');
    setDate(stock?.date ? stock.date.slice(0, 10) : '');
  }, [stock, show]);

  useEffect(() => {
    async function loadStocknames() {
      try {
        const res = await fetchStocknames();
        setStocksnames(Array.isArray(res) ? res : []);
      } catch (e) {
        setStocksnames([]);
      }
    }
    loadStocknames();
  }, []);

  const stockNameValue =
    name
      ? { value: name, label: name }
      : null;

  if (!show || !stock) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div
        className="rounded-2xl shadow-lg p-6 w-[80vw] max-w-lg text-left bg-white"
        style={{}}
      >
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Edit Stock Details</h2>
        <form
          onSubmit={e => {
            e.preventDefault();
            let cat_id = undefined;
            let newCategories: StockCategoryObj[] = [];
            if (Array.isArray(stock.categories) && stock.categories.length > 0) {
              newCategories = stock.categoryObjs
                ? stock.categoryObjs.map(catObj => {
                  if (
                    (stock.categories[0].id !== undefined && catObj.id === stock.categories[0].id) ||
                    (stock.categories[0].id === undefined && catObj === stock.categories[0])
                  ) {
                    return {
                      ...catObj,
                      stock_name: String(name),
                      category: String(category),
                      unit: String(unit),
                      perUnitCharge: String(pricePerUnit),
                      total_amount: String(totalAmount),
                      indirect_charges: String(indirectCharges),
                    };
                  } else {
                    return { ...catObj };
                  }
                })
                : stock.categories.map((catObj) => ({
                  ...catObj,
                  stock_name: String(name),
                  category: String(category),
                  unit: String(unit),
                  perUnitCharge: String(pricePerUnit),
                  total_amount: String(totalAmount),
                  indirect_charges: String(indirectCharges),
                }));
              cat_id = stock.categories?.[0]?.id;
            } else {
              newCategories = [{
                stock_name: name,
                category: category,
                unit: unit,
                perUnitCharge: String(pricePerUnit),
                total_amount: String(totalAmount),
                indirect_charges: String(indirectCharges),
              }];
            }
            onUpdate({
              id: stock.id,
              cat_id,
              name,
              party_id: stock.party_id,
              categories: JSON.stringify(newCategories),
              category,
              unit,
              price_per_unit: pricePerUnit,
              total_amount: totalAmount,
              indirect_charges: indirectCharges,
              date
            });
          }}
        >
          <div className="flex flex-col gap-4">
            {/* --- Replace input for Stock Name with react-select dropdown --- */}
            <div className="flex flex-col">
              <label htmlFor={`stockname-edit-modal`} className="text-[13px]">
                Stock Name
              </label>
              <Select
                inputId={`stockname-edit-modal`}
                options={stocksnames.map(stock => ({
                  value: stock.name,
                  label: stock.name,
                }))}
                value={stockNameValue}
                onChange={selected => {
                  setName(selected?.value || "");
                }}
                placeholder="Search Stock Name..."
                className="text-[13px]"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "8px",
                    borderColor: "#D0D5DD",
                    minHeight: "40px",
                    width: "100%"
                  })
                }}
              />
            </div>
            {/* --- Category (as before) --- */}
            <div>
              <label className="text-sm mb-1 text-gray-700 font-medium">Category</label>
              <input
                className="border rounded px-2 py-1 w-full"
                type="text"
                value={category}
                onChange={e => setCategory(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm mb-1 text-gray-700 font-medium">Unit</label>
              <input
                className="border rounded px-2 py-1 w-full"
                type="text"
                value={unit}
                onChange={e => setUnit(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm mb-1 text-gray-700 font-medium">Price per Unit</label>
              <input
                className="border rounded px-2 py-1 w-full"
                type="number"
                step="any"
                value={pricePerUnit}
                onChange={e => setPricePerUnit(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm mb-1 text-gray-700 font-medium">Total Amount</label>
              <input
                className="border rounded px-2 py-1 w-full"
                type="number"
                step="any"
                value={totalAmount}
                onChange={e => setTotalAmount(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm mb-1 text-gray-700 font-medium">Indirect Charges</label>
              <input
                className="border rounded px-2 py-1 w-full"
                type="number"
                step="any"
                value={indirectCharges}
                onChange={e => setIndirectCharges(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm mb-1 text-gray-700 font-medium">Date of Purchase</label>
              <input
                className="border rounded px-2 py-1 w-full"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-row gap-2 mt-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition h-[38px] bg-gradient-to-r from-[#0d432d] to-[#FF9500] py-2 rounded-[8px]"
              >
                Save
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition h-[38px]"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
// --- End of Edit Stock Modal ---

// ... UpdateStatusModal, CurrentStatusBar remain unchanged ...

const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({ show, stock, onClose, onUpdate }) => {
  // ... unchanged ...
  const [currentPrice, setCurrentPrice] = useState<string>(stock?.current_price ?? "");
  const [currentPriceDate, setCurrentPriceDate] = useState<string>(
    stock?.current_price_date
      ? typeof stock.current_price_date === 'string'
        ? stock.current_price_date.slice(0, 10)
        : new Date(stock.current_price_date).toISOString().slice(0, 10)
      : ""
  );
  const [dividendAmount, setDividendAmount] = useState<string>("");

  React.useEffect(() => {
    let price = stock?.current_price ?? "";
    let date = stock?.current_price_date
      ? typeof stock.current_price_date === 'string'
        ? stock.current_price_date.slice(0, 10)
        : new Date(stock.current_price_date).toISOString().slice(0, 10)
      : "";
    let sundry_creditors = stock?.sundry_creditors != null ? String(stock.sundry_creditors) : "";
    setCurrentPrice(price);
    setCurrentPriceDate(date);
    setDividendAmount(sundry_creditors); // Reset the dividend input when stock changes
  }, [stock]);

  if (!show || !stock) return null;

  // ... render code unchanged
  // ...
  const infoRows = [
    { label: "Category", value: Array.isArray(stock.categories) && stock.categories.length > 0 ? stock.categories[0].category : "" },
    { label: "Stock Name", value: stock.name || "" },
    { label: "Date of Purchase", value: stock.date ? new Date(stock.date).toLocaleDateString('en-GB') : "" },
    { label: "Party Name", value: stock.party_name ?? "" },
    { label: "Unit", value: stock.unit ?? "" },
    { label: "Price per unit", value: stock.price_per_unit ?? "" },
    { label: "Total Amount", value: stock.total_amount ?? "" },
    { label: "Indirect Expenses", value: stock.indirect_charges ?? "" },
    { label: "Current Price", value: stock.current_price ?? "" },
    {
      label: "Current Price Date", value: stock.current_price_date
        ? (() => {
          if (typeof stock.current_price_date === 'string') {
            const n = new Date(stock.current_price_date);
            return isNaN(n.getTime()) ? stock.current_price_date : n.toLocaleDateString('en-GB');
          }
          try {
            return new Date(stock.current_price_date).toLocaleDateString('en-GB');
          } catch {
            return "";
          }
        })()
        : ""
    },
    { label: "Withdrawal", value: stock.withdrawal ?? "" },
    { label: "Sale", value: stock.sale ?? "" },
    { label: "Current Status", value: stock.current_status ?? "" },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div
        className="rounded-2xl shadow-lg p-6 w-[80vw] max-w-full text-left"
        style={{ background: "linear-gradient(99deg, #ffffff, #1CAC70 41%)" }}
      >
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Update Stock Status</h2>
        <form
          onSubmit={e => {
            e.preventDefault();
            let cat_id = undefined;
            if (Array.isArray(stock.categories) && stock.categories[0] && stock.categories[0].id !== undefined) {
              cat_id = stock.categories[0].id;
            }
            onUpdate({
              id: stock.id,
              cat_id,
              current_price: currentPrice,
              current_price_date: currentPriceDate,
              dividend_amount: dividendAmount
            });
          }}
        >
          <div className="flex flex-row gap-0">
            <div className="flex-1 min-w-[200px] pr-4">
              <div className="overflow-y-auto">
                <table className="table-auto w-full text-sm border rounded">
                  <tbody>
                    {infoRows.map(({ label, value }) => (
                      <tr key={label} className="border-b last:border-b-0">
                        <td className="py-1 px-2 text-gray-500 w-1/2">{label}</td>
                        <td className="py-1 px-2 text-gray-900">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex-shrink-0 mx-3 flex items-stretch">
              <div className="border-l-2 border-dashed border-gray-300 h-auto"></div>
            </div>
            <div className="flex flex-col justify-center flex-1 min-w-[200px]">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <label className="text-sm mb-1 text-gray-700 font-medium">Current Price</label>
                  <input
                    className="border rounded px-2 py-1"
                    type="number"
                    step="any"
                    value={currentPrice}
                    onChange={e => setCurrentPrice(e.target.value)}
                    required
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm mb-1 text-gray-700 font-medium">Dividend Amount</label>
                  <input
                    className="border rounded px-2 py-1"
                    type="number"
                    step="any"
                    value={dividendAmount}
                    onChange={e => setDividendAmount(e.target.value)}
                    placeholder="Enter dividend amount"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm mb-1 text-gray-700 font-medium">Current Price Date</label>
                  <input
                    className="border rounded px-2 py-1"
                    type="date"
                    value={currentPriceDate}
                    onChange={e => setCurrentPriceDate(e.target.value)}
                    required
                  />
                </div>
                <div className="flex flex-row gap-2 mt-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition h-[38px] bg-gradient-to-r from-[#0d432d] to-[#FF9500] py-2 rounded-[8px]"
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition h-[38px]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
// ...CurrentStatusBar unchanged...

const CurrentStatusBar: React.FC<{ totalAmount?: number | string, currentPrice?: number | string }> = ({ totalAmount, currentPrice }) => {
  let t = Number(totalAmount ?? 0);
  let c = Number(currentPrice ?? 0);

  let percent = 0;
  let statusLabel = "";
  let diffPercent = 0;
  let isMax = false;
  let isMin = false;

  if (!isNaN(t) && t !== 0 && !isNaN(c)) {
    percent = (c / t) * 100;

    if (c === t) {
      statusLabel = "At Par";
      percent = 100;
      isMax = false;
      isMin = false;
    } else if (c > t) {
      diffPercent = ((c - t) / t) * 100;
      statusLabel = `More by ${diffPercent.toFixed(2)}%`;
      percent = 100;
      isMax = true;
      isMin = false;
    } else if (c < t) {
      diffPercent = ((t - c) / t) * 100;
      statusLabel = `Less by ${diffPercent.toFixed(2)}%`;
      percent = Math.max(0, percent);
      isMax = false;
      isMin = true;
    }
  }

  return (
    <div className="flex flex-col items-center w-full min-w-[80px]">
      <div className="w-full bg-gray-200 rounded h-2 relative overflow-hidden">
        <div
          className={`h-full rounded ${isMax ? "bg-green-500" : isMin ? "bg-red-500" : "bg-yellow-400"}`}
          style={{ width: `${percent}%`, transition: 'width 0.3s' }}
        />
      </div>
      <div className="flex justify-between mt-1 w-full text-[10px]">
        <span
          className={`${isMax
              ? "text-green-700 font-bold"
              : isMin
                ? "text-red-700 font-bold"
                : "text-yellow-800"
            }`}
        >
          {isMax ? "Max" : isMin ? "Min" : `${percent.toFixed(0)}%`}
        </span>
        <span className="ml-auto text-gray-500">
          {isNaN(c) ? "" : `₹${c.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
        </span>
      </div>
    </div>
  );
};

// ----- INVESTMENT TYPE BUTTONS AND FILTERING -----

const INVESTMENT_TYPE_OPTIONS = [
  // { label: "All", value: "all" },
  { label: "Structure", value: "structure" },
  { label: "Direct", value: "direct" },
  // { label: "Stocks", value: "stock" }
];

function getInvestmentTypeForStock(stock: any): string {
  // Try to extract investment_type from either categoryObjs or categories
  if (Array.isArray(stock.categoryObjs) && stock.categoryObjs[0] && stock.categoryObjs[0].investment_type) {
    return String(stock.categoryObjs[0].investment_type).toLowerCase();
  }
  if (
    Array.isArray(stock.categories) &&
    stock.categories[0] &&
    stock.categories[0].investment_type
  ) {
    return String(stock.categories[0].investment_type).toLowerCase();
  }
  // fallback, treat as "structure" if not present (optional)
  return "";
}

function Stocks(props: Props) {
  const { } = props;
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows] = useState<number>(10);
  const [parties, setParties] = useState<Party[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [sellStocks, setSellStocks] = useState<SellStockApiItem[]>([]);
  const [reload, setReload] = useState(0);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [StoreOfCatId, setStoreOfCatId] = useState<number | null>(null);

  // changed default to 'all'
  const [InvestmentTypeTab, setInvestmentTypeTab] = useState('all');
  const navigate = useNavigate();

  const [showColumnModal, setShowColumnModal] = useState<boolean>(false);

  // For update status modal
  const [showUpdateStatus, setShowUpdateStatus] = useState<boolean>(false);
  const [selectedStockForUpdate, setSelectedStockForUpdate] = useState<Stock | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  // For edit modal
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedStockForEdit, setSelectedStockForEdit] = useState<Stock | null>(null);
  const [isUpdatingStock, setIsUpdatingStock] = useState<boolean>(false);

  // State for update stock records (history, current prices)
  const [updateStockRecords, setUpdateStockRecords] = useState<any[]>([]);

  const [showActionMenuId, setShowActionMenu] = useState<number | null>();

  const [activeFilterDropdown, setActiveFilterDropdown] = useState<string | null>(null);
  const filterDropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // --- COLUMN SELECTION FOR TABLE VIEW ---
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    const visibility: Record<string, boolean> = {};
    TABLE_COLUMNS.forEach(col => { visibility[col.key] = true; });
    return visibility;
  });
  const handleAddStock = (partyId: number | string) => navigate(`/add-stock?party=${partyId}`);
  const handleSellStock = (partyId: number | string) => navigate(`/sell-stock?party=${partyId}`);
  const handleWithdraw = (partyId: number | string) => navigate(`/withdraw?party=${partyId}`);
  const handleAddAmount = (partyId: number | string) => navigate(`/add-amount?party=${partyId}`);
  const handleUpdateStockValue = (partyId: number | string) => navigate(`/update-stock-value?party=${partyId}`);
  const handleViewHistory = (partyId: number | string) => navigate(`/usershistory?party=${partyId}`);
  const handleViewBalanceSheet = (partyId: number | string) => navigate(`/balancesheet?party=${partyId}`);
  const [stocksnames, setStocksnames] = useState<StockNames[]>([]);
  const fetchParties = useCallback(async () => {
    const data = await partiesApi();
    const cleanedData = Array.isArray(data)
      ? data.map((item: any) => ({
        ...item,
        categories:
          typeof item.categories === "string"
            ? (() => {
              try {
                const parsed = JSON.parse(item.categories);
                return Array.isArray(parsed) ? parsed : [item.categories];
              } catch {
                return [item.categories];
              }
            })()
            : Array.isArray(item.categories)
              ? item.categories
              : [],
      }))
      : [];
    setParties(cleanedData);
  }, []);
  const fetchStocksNames = async () => {
    try {
      const res = await fetchStocknames();
      const data = await res;
      setStocksnames(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setStocksnames([]);
    } finally {

    }
  };

  const fetchUpdateStockRecords = useCallback(async () => {
    try {
      const updateData = await updatestocksApi();
      setUpdateStockRecords(Array.isArray(updateData) ? updateData : []);
    } catch {
      setUpdateStockRecords([]);
    }
  }, []);

  const fetchSellStocks = useCallback(async () => {
    try {
      const res = await sellstocksApi();
      setSellStocks(Array.isArray(res) ? res : []);
    } catch {
      setSellStocks([]);
    }
  }, []);

  const fetchStocks = useCallback(async () => {
    const data = await stocksApi();
    let cleanedData: any[] = [];

    if (Array.isArray(data)) {
      data.forEach((item: any) => {
        let catObjs: StockCategoryObj[] = [];
        if (typeof item.categories === "string") {
          try {
            const parsed = JSON.parse(item.categories);
            catObjs = Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            catObjs = [];
          }
        } else if (Array.isArray(item.categories)) {
          catObjs = item.categories;
        }
        if (!Array.isArray(catObjs)) catObjs = [];
        if (catObjs.length > 0) {
          catObjs.forEach((catObj) => {
            cleanedData.push({
              ...item,
              categories: [catObj],
              categoryObjs: catObjs,
              cat_id: catObj.id,
              name: catObj.stock_name,
              category: catObj.category,
              unit: catObj.unit,
              price_per_unit: catObj.perUnitCharge,
              total_amount: catObj.total_amount,
              indirect_charges: catObj.indirect_charges,
              current_price: "",
              current_price_date: "",
            });
          });
        } else {
          cleanedData.push({
            ...item,
            categories: [],
            categoryObjs: [],
            cat_id: undefined,
            name: "",
            category: "",
            unit: "",
            price_per_unit: "",
            total_amount: "",
            indirect_charges: "",
            current_price: "",
            current_price_date: "",
          });
        }
      });
    }

    setStocks(prev => {
      if (Array.isArray(updateStockRecords) && updateStockRecords.length > 0) {
        return cleanedData.map(stockObj => {
          const lastRecord = findLatestStockHistory(updateStockRecords, stockObj.id, stockObj.cat_id);
          if (lastRecord) {
            return {
              ...stockObj,
              current_price: lastRecord.current_price,
              current_price_date: lastRecord.current_price_date,
            };
          } else {
            return {
              ...stockObj,
              current_price: "",
              current_price_date: "",
            };
          }
        });
      }
      return cleanedData;
    });
  }, [updateStockRecords]);

  const fetchWithdrawals = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/withdrawals`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) return setWithdrawals([]);
      const data = await res.json();
      setWithdrawals(Array.isArray(data) ? data : []);
    } catch {
      setWithdrawals([]);
    }
  }, []);

  const getVisibleColumns = () => TABLE_COLUMNS.filter(col => visibleColumns[col.key]);

  // Withdrawal matching logic for your API shape (catrgories is stringified array)
  useEffect(() => {
    if (!Array.isArray(withdrawals)) return;
    function parseCategoriesField(val: any): string {
      if (!val || typeof val !== "string") return "";

      try {
        const arr = JSON.parse(val);

        if (Array.isArray(arr) && arr.length > 0) {
          const nested = JSON.parse(arr[0]);

          if (Array.isArray(nested) && nested.length > 0) {
            const item = nested[0];

            if (item.category && item.stockName) {
              return `${item.category}__${item.stockName}`;
            }
          }
        }

        return "";
      } catch {
        return "";
      }
    }

    function stockInvestmentType(stock: any): string {
      if (Array.isArray(stock.categoryObjs) && stock.categoryObjs.length > 0) {
        if (stock.categoryObjs[0].investment_type) {
          return String(stock.categoryObjs[0].investment_type).toLowerCase();
        }
      }
      if (
        Array.isArray(stock.categories) &&
        stock.categories.length > 0 &&
        stock.categories[0].investment_type
      ) {
        return String(stock.categories[0].investment_type).toLowerCase();
      }
      return "";
    }
    const withdrawalMap = new Map();
    withdrawals.forEach(w => {
      const withdrawInvType = parseCategoriesField(w.catrgories);
      const key =
        `${w.party_id}___${withdrawInvType}`;
      withdrawalMap.set(key, w.amount);
    });
    setStocks(prev =>
      prev.map(stock => {
        let invType = stockInvestmentType(stock);
        const wKey = `${stock.party_id}___${stock.category}__${stock.name}`;
        return {
          ...stock,
          withdrawal: withdrawalMap.get(wKey) ?? "",
        };
      })
    );
  }, [withdrawals]);

  useEffect(() => {
    const run = async () => {
      await fetchStocksNames();
      await fetchSellStocks();
      await fetchParties();
      await fetchUpdateStockRecords();
      await fetchWithdrawals();
    };
    run();
  }, [fetchSellStocks, fetchParties, fetchUpdateStockRecords, fetchWithdrawals, reload]);

  useEffect(() => {
    fetchStocks();
  }, [fetchStocks, reload, updateStockRecords]);

  useEffect(() => {
    const handleClick = (event: any) => {
      let found = false;
      if (activeFilterDropdown && filterDropdownRefs.current[activeFilterDropdown]) {
        const ref = filterDropdownRefs.current[activeFilterDropdown];
        if (ref && ref.contains(event.target)) found = true;
      }
      if (!found) setActiveFilterDropdown(null);
    };
    if (activeFilterDropdown) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [activeFilterDropdown]);

  // Sale matching for mergedStocks
  const mergedStocks = React.useMemo(() => {
    if (!Array.isArray(stocks) || stocks.length === 0) return [];
    if (!Array.isArray(sellStocks)) return [];

    const saleMap: Record<string, number> = {};

    sellStocks.forEach((s) => {
      if (
        s.stock_id != null &&
        s.party_id != null &&
        s.category &&
        !isNaN(Number(s.sell_amount))
      ) {
        const key = `${s.stock_id}_${s.party_id}_${s.category}`;
        saleMap[key] = (saleMap[key] || 0) + Number(s.sell_amount);
      }
    });

    return stocks.map((stock) => {
      const partyObj = parties.find((p) => String(p.id) === String(stock.party_id));
      const key = `${stock.categories[0].id}_${stock.party_id}_${stock.category}`;
      const sale = saleMap[key] ?? "";
      return {
        ...stock,
        party_name: partyObj ? partyObj.name : "",
        sale,
      };
    });
  }, [stocks, parties, sellStocks]);

  // FILTER BY INVESTMENT TYPE TAB ---------
  const filteredByTypeStocks = React.useMemo(() => {
    if (InvestmentTypeTab === "all")
      return mergedStocks;

    return mergedStocks.filter(stock => {
      const type = getInvestmentTypeForStock(stock);
      if (!type && InvestmentTypeTab === "structure") {
        // Show legacy, structure-default
        return true;
      }
      return type === InvestmentTypeTab;
    });
  }, [mergedStocks, InvestmentTypeTab]);

  const filterDropdownOptions: Record<string, string[]> = {
    category: getUnique(filteredByTypeStocks, 'category'),
    name: getUnique(filteredByTypeStocks, 'name'),
    unit: getUnique(filteredByTypeStocks, 'unit'),
    price_per_unit: getUnique(filteredByTypeStocks, 'price_per_unit'),
    total_amount: getUnique(filteredByTypeStocks, 'total_amount'),
    indirect_charges: getUnique(filteredByTypeStocks, 'indirect_charges'),
    current_price: getUnique(filteredByTypeStocks, 'current_price'),
    date: getUnique(filteredByTypeStocks, 'date'),
    current_price_date: getUnique(filteredByTypeStocks, 'current_price_date')
  };

  const allDateStrings = getUnique(filteredByTypeStocks, 'date').map((v) => {
    try {
      const d = new Date(v);
      if (!isNaN(d.valueOf())) {
        return d.toISOString().split('T')[0];
      }
    } catch { }
    return null;
  }).filter(Boolean) as string[];

  const allCPDateStrings = getUnique(filteredByTypeStocks, 'current_price_date').map((v) => {
    try { const d = new Date(v); if (!isNaN(d.valueOf())) { return d.toISOString().split('T')[0]; } } catch { }; return null;
  }).filter(Boolean) as string[];

  let filteredStocks = filteredByTypeStocks.filter(item => {
    const lSearch = searchTerm.toLowerCase();
    const getLower = (val: unknown) => typeof val === 'string' ? val.toLowerCase() : '';
    const byStockName = getLower(item.name).includes(lSearch);
    const byParty = getLower(item.party_name).includes(lSearch);

    let byCategory = false;
    if (Array.isArray(item.categories) && item.categories.length > 0) {
      byCategory = item.categories.some(
        (cat: any) => (cat.category && getLower(cat.category).includes(lSearch)) ||
          (cat.stock_name && getLower(cat.stock_name).includes(lSearch))
      );
    }

    const byUnit = getLower(item.unit).includes(lSearch);
    return byStockName || byParty || byCategory || byUnit;
  });

  filteredStocks = filteredStocks.filter((item) => {
    for (const col of TABLE_COLUMNS) {
      if (!filters[col.key]) continue;
      const filterValue = filters[col.key].toLowerCase().trim();
      if (!filterValue) continue;
      const val = (item as any)[col.key];

      if (col.key === 'date' || col.key === 'current_price_date') {
        const dateStr = val
          ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '';
        const dateIso = typeof val === "string" ? val.slice(0, 10) : '';
        if (!dateStr.toLowerCase().includes(filterValue) && !dateIso.includes(filterValue)) return false;
      } else if (col.key === "category") {
        if (
          !(
            Array.isArray(item.categories) &&
            item.categories.length > 0 &&
            (item.categories[0].category?.toLowerCase().includes(filterValue) || false)
          )
        ) {
          return false;
        }
      } else {
        if (!(val && val.toString().toLowerCase().includes(filterValue))) {
          return false;
        }
      }
    }
    return true;
  });

  const lastIndex = totalRows * currentPage;
  const firstIndex = lastIndex - totalRows;
  const currentDisplay = filteredStocks.slice(firstIndex, lastIndex);

  const deleteParty = async (id: number, storeid: number) => {
    try {
      const stockObj = stocks.find(stk => stk.id === id);
      if (!stockObj) {
        alert("Stock not found");
        return;
      }
      let categoryObjs: StockCategoryObj[] = [];
      if (typeof stockObj.categories === "string") {
        try {
          const parsed = JSON.parse(stockObj.categories as any);
          if (Array.isArray(parsed)) {
            categoryObjs = parsed;
          } else if (parsed && typeof parsed === "object") {
            categoryObjs = [parsed];
          }
        } catch {
          categoryObjs = [];
        }
      } else if (Array.isArray(stockObj.categoryObjs)) {
        categoryObjs = stockObj.categoryObjs;
      } else if (Array.isArray(stockObj.categories)) {
        categoryObjs = stockObj.categories as StockCategoryObj[];
      } else {
        categoryObjs = [];
      }

      // If only one category/row left, delete the entire stock row (use DELETE API)
      if (categoryObjs.length === 1) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: stockObj.id }),
        });

        if (response.ok) {
          setReload(prev => prev + 1);
        } else {
          const data = await response.json();
          alert(data.message || "Failed to delete stock");
        }
        return;
      }

      // Otherwise, remove just the selected category and update
      const newCategories = categoryObjs.filter(cat => String(cat.id) !== String(storeid));
      const bodyToSend = {
        id: stockObj.id,
        cat_id: storeid,
        name: stockObj.name,
        party_id: stockObj.party_id,
        categories: JSON.stringify(newCategories),
        category: '',
        unit: stockObj.unit || '',
        price_per_unit: stockObj.price_per_unit || '',
        total_amount: stockObj.total_amount || '',
        indirect_charges: stockObj.indirect_charges || '',
        date: stockObj.date ? stockObj.date.split('T')[0] : '',
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyToSend),
      });

      if (response.ok) {
        setReload(prev => prev + 1);
      } else {
        const data = await response.json();
        alert(data.message || "Failed to update stock");
      }
    } catch (error) {
      alert("Error deleting stock category");
    }
  };
  const updateStockStatus = async ({ id, cat_id, current_price, current_price_date, dividend_amount }: { id: number; cat_id?: number; current_price: string; current_price_date: string; dividend_amount?: string }) => {
    setIsUpdatingStatus(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/updatestock`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stock_id: id,
          cat_id: cat_id,
          current_price,
          current_price_date,
          sundry_creditors: dividend_amount ?? undefined
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data?.message || "Failed to update status");
      } else {
        setShowUpdateStatus(false);
        setSelectedStockForUpdate(null);
        setReload(r => r + 1);
      }
    } catch (error) {
      alert("Update error");
    }
    setIsUpdatingStatus(false);
  };

  const updateStockDetails = async ({
    id,
    cat_id,
    name,
    party_id,
    category,
    categories,
    unit,
    price_per_unit,
    total_amount,
    indirect_charges,
    date,
  }: {
    id: number;
    cat_id?: number;
    name: string;
    party_id: number;
    categories: string;
    category: string;
    unit: string;
    price_per_unit: string | number;
    total_amount: string | number;
    indirect_charges: string | number;
    date: string;
  }) => {
    setIsUpdatingStock(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
        method: 'put',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: id,
          cat_id,
          name,
          party_id,
          categories,
          category,
          unit,
          price_per_unit,
          total_amount,
          indirect_charges,
          date
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data?.message || "Failed to update stock details");
      } else {
        setShowEditModal(false);
        setSelectedStockForEdit(null);
        setReload(r => r + 1);
      }
    } catch (error) {
      alert("Update error");
    }
    setIsUpdatingStock(false);
  };

  return (
    <>
      <div className='p-6'>
        <div className='w-[100%]'>
          <div className='flex flex-wrap justify-between w-full'>
            {/* Highlight: Main header, bring Party Name to highlight */}
            <div className='flex items-center gap-4'>
              <h1 className='text-[28px] font-[600] text-[#1CAC70] tracking-wide flex items-center'>
                <span className="mr-2">
                  <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" fill="#FF9500" /><rect x="4" y="16" width="16" height="4" rx="2" fill="#1CAC70" /></svg>
                </span>
                Vendor Stocks
              </h1>
              <div className='bg-[#F9F5FF] p-2 rounded-[40px]'>
                <span className='text-[#6941C6] text-[14px] font-semibold'>{parties.length} vendors</span>
              </div>
            </div>
            <div className='flex items-center gap-4'>
              <button className='flex items-center gap-1 border-[1px] border-[#D0D5DD] bg-[#fff] py-2 px-4 rounded-[8px]'>
                <BiImport /> Import
              </button>
              <Link to='/add-stock'>
                <button className="flex items-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-4">
                  <FaPlus />Add Stock
                </button>
              </Link>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8"
                  onClick={() => window.location.href = '/withdraw'}
                >
                  Add Withdrawal
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#FF9500] to-[#1CAC70] py-2 rounded-[8px] px-8"
                  onClick={() => window.location.href = '/sell-stock'}
                >
                  Sell Stock
                </button>
              </div>
            </div>
          </div>
          <span className='block mt-1 text-[#667085] text-[15px]'>
            Keep track of stocks. <span className="font-semibold text-[#FF9500]">Party/Vendor</span> is prioritized.
          </span>
        </div>

        {/* ===== INVESTMENT TYPE SWITCH BUTTONS ===== */}
        <div className="mt-6 mb-4 flex flex-wrap items-center gap-3">
          <span className="mr-2 text-sm font-medium text-gray-700">Investment Type:</span>
          {INVESTMENT_TYPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={
                "px-4 py-2 rounded-[20px] text-sm font-semibold border " +
                (InvestmentTypeTab === opt.value
                  ? "bg-gradient-to-r from-[#FF9500] to-[#1CAC70] text-white border-[#FF9500]"
                  : "bg-[#f9fafb] text-[#1CAC70] hover:bg-[#FFF0E0] border-[#EDEDED]")
              }
              style={{ transition: "all 0.15s" }}
              onClick={() => {
                setInvestmentTypeTab(opt.value);
                setCurrentPage(1);
              }}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
        {/* ===== END INVESTMENT TYPE BUTTONS ===== */}

        {/* ====== COLUMN SELECTION UI ====== */}
        <div className="my-4 flex items-center">
          <button
            className="flex items-center gap-2 bg-gradient-to-r from-[#F9F5FF] to-[#FFFAE5] border border-[#D0D5DD] px-5 py-2 rounded-lg text-[#131A20] font-semibold text-sm shadow-sm hover:shadow transition"
            onClick={() => setShowColumnModal(true)}
            type="button"
          >
            <svg className="text-[#A891E6]" width="18" height="18" fill="none" viewBox="0 0 20 20"><rect width="18" height="18" rx="4" fill="#A891E6" /><path d="M6 8l4 4 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Manage Columns
          </button>
          {showColumnModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-xl border border-[#efeded] shadow-2xl w-full max-w-sm p-6 relative animate-fadeIn">
                <div className="flex items-center justify-between pb-2 mb-4 border-b">
                  <div className="flex items-center gap-2">
                    <svg width="19" height="19" className="text-[#A891E6]" fill="none" viewBox="0 0 20 20"><rect width="18" height="18" rx="4" fill="#A891E6" /><path d="M6 8l4 4 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    <span className="text-base font-bold text-[#1CAC70]">Choose Columns to Display</span>
                  </div>
                  <button
                    className="text-[#FF9500] text-2xl font-bold px-2 focus:outline-none"
                    onClick={() => setShowColumnModal(false)}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
                <div className="flex flex-col gap-2 max-h-60 overflow-y-auto pt-1">
                  {TABLE_COLUMNS.map(col => (
                    <label
                      key={col.key}
                      className="flex items-center gap-2 px-3 py-2 rounded hover:bg-[#F9F5FF] text-sm font-medium cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="accent-[#A891E6] h-4 w-4"
                        checked={visibleColumns[col.key]}
                        onChange={e => {
                          setVisibleColumns(prev => ({
                            ...prev,
                            [col.key]: e.target.checked,
                          }));
                        }}
                      />
                      <span className={col.key === 'party_name' ? 'text-[#FF9500] font-bold' : ''}>{col.label}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-6">
                  <button
                    className="w-full py-2 rounded bg-[#1CAC70] text-white text-xs font-semibold hover:bg-[#16905D] transition"
                    type="button"
                    onClick={() => setVisibleColumns(TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}))}
                  >
                    Show All
                  </button>
                  <button
                    className="w-full py-2 rounded bg-[#FF9500] text-white text-xs font-semibold hover:bg-[#b86d07] transition"
                    type="button"
                    onClick={() => setVisibleColumns(TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: false }), {}))}
                  >
                    Hide All
                  </button>
                </div>
                <button
                  className="w-full mt-4 py-2 rounded bg-gray-100 text-[#FF9500] text-xs font-semibold hover:bg-[#f8efd9] transition"
                  type="button"
                  onClick={() => setShowColumnModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
        {/* ====== END COLUMN SELECTION UI ====== */}

        <div className='flex flex-col gap-4 mt-7 w-full'>
          <div className="flex items-center gap-3 w-full">
            <div className="w-[40%] bg-[#fff] h-[38px] flex items-center rounded-[8px] px-2 border-[#D0D5DD] border-[1px] gap-1 ">
              <CiSearch className="text-[#8488B7] text-[24px] cursor-pointer" />
              <input
                type="text"
                placeholder="Search by party, stock, or category..."
                className="w-full bg-transparent h-[32px] focus:outline-none "
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <button
              className="flex items-center gap-2 text-[13px] font-medium bg-[#FFFAE5] border border-[#FFD699] text-[#6941C6] px-2 py-1 rounded"
              onClick={() => setShowFilters(f => !f)}
              type="button"
            >
              <span>Filters</span>
              <svg width="16" height="16" fill="none" viewBox="0 0 15 15"><path fill="currentColor" fillRule="evenodd" d="M3.1 6.65a.5.5 0 0 1 .7-.1L7.5 9.29l3.69-2.74a.5.5 0 1 1 .6.8l-4 2.98a.5.5 0 0 1-.6 0l-4-2.98a.5.5 0 0 1-.1-.7z" clipRule="evenodd"></path></svg>
            </button>
          </div>
          {showFilters && (
            <div className="flex flex-wrap items-end gap-4 bg-[#F7F7FB] p-4 mt-2 rounded-2xl border">
              {[...TABLE_COLUMNS].filter(col =>
                [
                  'party_name',
                  'category',
                  'name',
                  'unit',
                  'date',
                  'current_price_date',
                  'price_per_unit'
                ].includes(col.key)
              ).sort((a, b) => {
                if (a.key === 'party_name') return -1;
                if (b.key === 'party_name') return 1;
                return 0;
              }).map((col) => {
                const isDate = col.key === "date" || col.key === "current_price_date";
                return (
                  <div
                    key={col.key}
                    className="flex flex-col relative"
                    ref={el => {
                      filterDropdownRefs.current[col.key] = el as HTMLDivElement | null;
                    }}
                  >
                    <label className={`text-xs mb-1 font-medium ${col.key === 'party_name' ? 'text-[#FF9500]' : 'text-[#7B7B95]'}`}>
                      {col.label}
                    </label>
                    {!isDate ? (
                      <div className="relative">
                        <input
                          className={`border rounded px-2 py-1 text-sm min-w-[180px] bg-white ${col.key === 'party_name' ? 'border-[#FF9500] font-bold bg-[#FFF4E0]' : ''}`}
                          type="text"
                          placeholder={`Filter by ${col.label}`}
                          value={filters[col.key] || ""}
                          onChange={e => {
                            setFilters(f => ({ ...f, [col.key]: e.target.value }));
                            setCurrentPage(1);
                          }}
                          onClick={() => setActiveFilterDropdown(col.key)}
                          onFocus={() => setActiveFilterDropdown(col.key)}
                          readOnly={Array.isArray(filterDropdownOptions[col.key]) && filterDropdownOptions[col.key].length > 0}
                          style={col.key === 'party_name' ? { boxShadow: '0 0 0 2px #FF950033' } : {}}
                        />
                        {activeFilterDropdown === col.key && Array.isArray(filterDropdownOptions[col.key]) && filterDropdownOptions[col.key].length > 0 && (
                          <div className="absolute left-0 top-full mt-1 w-full bg-white z-20 shadow rounded text-sm border max-h-44 overflow-auto">
                            {filterDropdownOptions[col.key].map(opt => (
                              <div
                                key={opt}
                                className="px-3 py-1 cursor-pointer hover:bg-[#F0F0FB]"
                                onMouseDown={e => {
                                  setFilters(f => ({ ...f, [col.key]: opt }));
                                  setActiveFilterDropdown(null);
                                  setCurrentPage(1);
                                }}
                              >
                                {opt}
                              </div>
                            ))}
                            <div
                              className="px-3 py-1 text-right text-xs text-blue-400 cursor-pointer underline"
                              onMouseDown={e => {
                                setFilters(f => ({ ...f, [col.key]: '' }));
                                setActiveFilterDropdown(null);
                              }}
                            >
                              Clear
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          className="border rounded px-2 py-1 text-sm min-w-[180px] bg-white"
                          type="text"
                          placeholder={`Filter by ${col.label}`}
                          value={filters[col.key] || ""}
                          onClick={() => setActiveFilterDropdown(col.key)}
                          onFocus={() => setActiveFilterDropdown(col.key)}
                          readOnly
                        />
                        {activeFilterDropdown === col.key && (
                          <div className="absolute z-20 left-0 mt-2">
                            <Calendar
                              value={filters[col.key]}
                              onChange={val => {
                                setFilters(f => ({
                                  ...f, [col.key]: val,
                                }));
                                setActiveFilterDropdown(null);
                                setCurrentPage(1);
                              }}
                              highlightedDates={col.key === "date" ? allDateStrings : allCPDateStrings}
                            />
                            <div
                              className="mt-2 text-xs text-right text-blue-400 cursor-pointer underline"
                              onClick={() => {
                                setFilters(f => ({ ...f, [col.key]: '' }));
                                setActiveFilterDropdown(null);
                              }}
                            >
                              Clear filter
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
              <button
                className="ml-auto mt-6 px-4 py-1 rounded text-xs bg-[#FAD1B1] text-[#E86D0C] font-medium hover:bg-[#FFEEDB] border border-[#F9BB6A] transition"
                onClick={() => setFilters({})}
                type="button"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* REARRANGED TABLE: Party Name prioritized as first column */}
        <div className="max-w-full overflow-x-auto border border-gray-200 mt-4">
          <table className="w-full max-w-full border-collapse table-auto">
            <thead className="bg-[#FCFCFD]">
              <tr>
                {/* Only show selected columns */}
                {getVisibleColumns().map(col => (
                  <th
                    key={col.key}
                    className={
                      `px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap` +
                      (col.key === 'party_name' ? " text-[#FF9500] font-bold bg-[#FFF4E0]" : "")
                    }
                  >
                    {col.label}
                  </th>
                ))}
                {/* Always show action column (the one for buttons), if visible */}
                {visibleColumns && (<th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap"></th>)}
              </tr>
            </thead>
            <tbody>
              {currentDisplay.map((stockObj) => (
                <tr
                  key={`${stockObj.id}-${Array.isArray(stockObj.categories) && stockObj.categories.length > 0 ? stockObj.categories[0].category : ''}-${stockObj.name}`}
                  className="hover:bg-[#FFEEDB] group transition"
                >
                  {/* Table row: dynamically render cells by column config */}
                  {getVisibleColumns().map(col => {
                    // cell render logic per col.key
                    switch (col.key) {
                      case "party_name":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#FF9500] border-b whitespace-nowrap font-bold bg-[#FFF4E0]">
                            {stockObj.party_name}
                          </td>
                        );
                      case "category":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#101828] border-b whitespace-nowrap">
                            {Array.isArray(stockObj.categories) && stockObj.categories.length > 0 && stockObj.categories[0].category
                              ? stockObj.categories[0].category
                              : ""}
                          </td>
                        );
                      case "name":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#101828] border-b whitespace-nowrap">
                            {stockObj.name || ""}
                          </td>
                        );
                      case "date":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            {stockObj.date
                              ? new Date(stockObj.date).toLocaleDateString('en-GB')
                              : ""}
                          </td>
                        );
                      case "unit":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            {stockObj.unit ?? ""}
                          </td>
                        );
                      case "price_per_unit":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            ₹{" "}
                            {(typeof stockObj.price_per_unit === "number" || typeof stockObj.price_per_unit === "string")
                              ? Number(stockObj.price_per_unit).toLocaleString('en-IN', { maximumFractionDigits: 2 })
                              : ""}
                          </td>
                        );
                      case "total_amount":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            {typeof stockObj.total_amount === "number" || (typeof stockObj.total_amount === "string" && stockObj.total_amount !== undefined && stockObj.total_amount !== null && stockObj.total_amount !== "")
                              ? (
                                <>
                                  ₹{" "}
                                  {Number(stockObj.total_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                </>
                              ) : ""}
                          </td>
                        );
                      case "indirect_charges":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            {stockObj.indirect_charges ?? ""}
                          </td>
                        );
                      case "current_price":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            ₹{" "}
                            {(typeof stockObj.current_price === "number" || typeof stockObj.current_price === "string") && stockObj.current_price !== undefined && stockObj.current_price !== null && stockObj.current_price !== ""
                              ? Number(stockObj.current_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })
                              : ""}
                          </td>
                        );
                      case "current_price_date":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            {stockObj.current_price_date
                              ? (() => {
                                if (typeof stockObj.current_price_date === "string") {
                                  const n = new Date(stockObj.current_price_date);
                                  return isNaN(n.getTime())
                                    ? stockObj.current_price_date
                                    : n.toLocaleDateString('en-GB');
                                }
                                try {
                                  return new Date(stockObj.current_price_date).toLocaleDateString('en-GB');
                                } catch {
                                  return "";
                                }
                              })()
                              : ""}
                          </td>
                        );
                      case "withdrawal":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            ₹ {stockObj.withdrawal !== undefined && stockObj.withdrawal !== null
                              ? Number(stockObj.withdrawal).toLocaleString('en-IN', { maximumFractionDigits: 2 })
                              : "0"}
                          </td>
                        );
                      case "sale":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap">
                            {(stockObj.sale !== undefined && stockObj.sale !== null)
                              ? (
                                <>
                                  ₹{" "}
                                  {Number(stockObj.sale).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                </>
                              ) : ""}
                          </td>
                        );
                      case "current_status":
                        return (
                          <td key={col.key} className="px-4 py-3 text-sm text-[#667085] border-b whitespace-nowrap min-w-[90px]">
                            <CurrentStatusBar
                              totalAmount={stockObj.total_amount}
                              currentPrice={stockObj.current_price}
                            />
                          </td>
                        );
                      default:
                        return null;
                    }
                  })}

                  {/* Always show the action-buttons cell as the LAST column */}
                  <td className="px-4 py-3 text-sm border-b whitespace-nowrap align-middle">
                    <div className="flex flex-col gap-2 min-w-[180px]">
                      <div className="flex justify-start gap-1 mb-1">
                        <button
                          type="button"
                          title="Edit Stock"
                          onClick={() => {
                            setSelectedStockForEdit(stockObj);
                            setShowEditModal(true);
                          }}
                          className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 hover:bg-blue-50 group transition"
                        >
                          <FiEdit2 size={15} className="text-blue-500 group-hover:text-blue-700" />
                        </button>
                        <button
                          title="Delete Stock"
                          onClick={() => {
                            setDeleteId(stockObj.id ?? null);
                            setStoreOfCatId(stockObj.cat_id ?? null);
                            setShowConfirm(true);
                          }}
                          className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 hover:bg-red-50 group transition"
                        >
                          <MdDeleteOutline size={17} className="text-red-500 group-hover:text-red-700" />
                        </button>
                        <button
                          title="Update Status"
                          onClick={() => {
                            window.location.href = `/update-stock-value?party=${stockObj.party_id}`;
                          }}
                          className="flex items-center gap-1 px-3 h-8 rounded-full border border-green-200 bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 transition"
                        >
                          <span>Update Status</span>
                        </button>
                      </div>
                      <div className="flex flex-row flex-wrap gap-1">

                        <button
                          onClick={() => handleSellStock(stockObj.party_id)}
                          className="bg-blue-100 hover:bg-blue-200 border border-blue-200 text-blue-800 px-2 h-7 rounded-full text-xs font-medium transition"
                        >
                          Sell
                        </button>



                        <button
                          onClick={() => handleViewHistory(stockObj.party_id)}
                          className="bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-800 px-2 h-7 rounded-full text-xs font-medium transition"
                        >
                          History
                        </button>
                        <button
                          onClick={() => handleViewBalanceSheet(stockObj.party_id)}
                          className="bg-purple-100 hover:bg-purple-200 border border-purple-200 text-purple-800 px-2 h-7 rounded-full text-xs font-medium transition"
                        >
                          Bal. Sheet
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Edit Stock Modal */}
        <EditStockModal
          show={showEditModal}
          stock={selectedStockForEdit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedStockForEdit(null);
          }}
          onUpdate={updateStockDetails}
        />

        {/* Update Status Modal */}
        <UpdateStatusModal
          show={showUpdateStatus}
          stock={selectedStockForUpdate}
          onClose={() => {
            setShowUpdateStatus(false);
            setSelectedStockForUpdate(null);
          }}
          onUpdate={updateStockStatus}
        />

        {/* Confirm Deletion Modal */}
        {showConfirm && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-80 text-center">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Delete Product?</h2>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete this Party? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowConfirm(false);
                    setDeleteId(null);
                    setStoreOfCatId(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (deleteId !== null && StoreOfCatId !== null) {
                      await deleteParty(deleteId, StoreOfCatId);
                      setShowConfirm(false);
                      setDeleteId(null);
                      setStoreOfCatId(null);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 space-x-3 relative z-0">
          <span className="text-[#344054] text-[14px]">
            Page {currentPage} of {Math.max(1, Math.ceil(filteredStocks.length / totalRows))}
          </span>
          <div className='flex gap-2'>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="px-3 py-1 bg-[#fff] disabled:opacity-50 text-[#344054] hover:bg-gray-300 transition border-[#D0D5DD] border-[1px] rounded-[6px] text-[14px]"
            >
              Previous
            </button>
            <button
              disabled={
                currentPage === Math.ceil(filteredStocks.length / totalRows) ||
                filteredStocks.length === 0
              }
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="px-3 py-1 bg-[#fff] disabled:opacity-50 hover:bg-gray-300 transition border-[#D0D5DD] border-[1px] rounded-[6px] text-[14px] text-[#344054]"
            >
              Next
            </button>
          </div>
        </div>

        {(isUpdatingStatus || isUpdatingStock) && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <div className="bg-white border rounded-xl px-4 py-2 text-green-800 shadow-lg">
              Updating...
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Stocks
